<template>
    <div class="col orders-search-results-vue" data-test="search-results-container">
        <!-- Search Summary Portal Content -->
        <portal to="searchResultsSummary">
            <div class="d-flex mt-3" data-test="search-summary">
                <div v-if="!_isTeam" class="nowrap ml-5 text-white" data-test="summary-teams">
                    <div class="text-sm opacity-75"># OF TEAM / VENUES</div>
                    <div class="text-lg">{{ meta.team_count || 0 }}</div>
                </div>
                <div v-if="!_isAdvertiser" class="nowrap ml-5 text-white" data-test="summary-advertisers">
                    <div class="text-sm opacity-75"># OF ADVERTISERS</div>
                    <div class="text-lg">{{ meta.advertiser_count || 0 }}</div>
                </div>
                <div class="nowrap ml-5 text-white" data-test="summary-assets">
                    <div class="text-sm opacity-75"># OF ASSETS</div>
                    <div class="text-lg">{{ meta.asset_count || 0 }}</div>
                </div>
                <div class="nowrap ml-5 text-white" data-test="summary-orders">
                    <div class="text-sm opacity-75"># OF ORDERS</div>
                    <div class="text-lg">{{ pagination.total || 0 }}</div>
                </div>
            </div>
        </portal>

        <div class="d-flex align-items-center mb-2">
            <div class="p-2 pr-5">
                <h4 class="mb-0 text-uppercase">{{ title }}</h4>
            </div>
        </div>

        <div class="d-flex align-items-center mb-2">
            <!-- Status Key -->
            <div v-if="icons && icons.length" data-test="status-key">
                <order-status-key
                    :icons="icons"
                    :with-links="false"
                    :user-role="_userType"
                ></order-status-key>
            </div>

            <!-- Download/Share buttons -->
            <div class="ml-auto d-flex" data-test="download-share-buttons">
                <download-order-items
                    download-type="search"
                    :query="downloadQuery"
                ></download-order-items>
                <share-order-items
                    share-type="search"
                    :query="downloadQuery"
                ></share-order-items>
            </div>

            <!-- Status Filter -->
            <div class="ml-1 select-blue" data-test="status-filter">
                <multiselect
                    v-model="statusFilter"
                    :options="statusOptions"
                    placeholder="Status"
                    :allow-empty="true"
                    :show-labels="false"
                    @input="onStatusFilterChange"
                ></multiselect>
            </div>

            <!-- Per Page -->
            <div class="ml-1 select-blue" data-test="per-page-select">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    @input="onPerPageChange"
                ></multiselect>
            </div>
        </div>

        <div class="row">
            <div class="col">
                <div class="jgile-table" data-test="orders-table">
                    <!-- Loading State -->
                    <div v-if="loading" class="text-center py-5">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                    </div>

                    <!-- Table -->
                    <table v-else class="table table-a">
                        <thead>
                        <tr>
                            <th :colspan="_isAdvertiser ? 2 : 1" data-test="sort-order-number">
                                <sort-button
                                    field="uuid"
                                    label="Order #"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th v-if="!_isAdvertiser" data-test="sort-advertiser">
                                <sort-button
                                    field="advertiser_name"
                                    label="Advertiser"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th data-test="sort-assets">
                                <sort-button
                                    field="assets_count"
                                    label="Assets"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th data-test="sort-run-dates">
                                <sort-button
                                    field="start_date"
                                    label="Run Date(s)"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th data-test="sort-units">
                                <sort-button
                                    field="units"
                                    label="Units"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th data-test="sort-price">
                                <sort-button
                                    field="amount"
                                    label="Price"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th colspan="2" class="actions"><span class="column-title"></span></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr class="row-spacer">
                            <td colspan="8"></td>
                        </tr>
                        </tbody>
                        <orders-table-order
                            v-for="order in orders"
                            :key="keyPrefix + order.uuid + 'OrderRow'"
                            :order="order"
                            :user-role="_userType"
                            :disable-actions="true"
                            :denied-only="false"
                            @refresh="loadOrders"
                            @open-review="handleOpenReview"
                            @open-view-creative="handleOpenViewCreative"
                        />
                    </table>

                    <!-- Empty State -->
                    <div v-if="!loading && orders.length === 0" class="text-center py-5">
                        <p class="text-muted">No orders found.</p>
                    </div>

                    <!-- Pagination -->
                    <div v-if="pagination.last_page > 1" class="d-flex justify-content-between my-2">
                        <vue-pagination
                            :current-page="pagination.current_page"
                            :last-page="pagination.last_page"
                            @page-change="goToPage"
                        />
                    </div>
                </div>

                <!-- Disclaimer -->
                <div class="mt-3" data-test="search-disclaimer">
                    <div class="alert alert-info">
                        This order may have assets which do not meet your search results.
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import OrdersTableApi from '@js/services/OrdersTableApi';
import SortButton from './partials/SortButton.vue';
import VuePagination from './partials/VuePagination.vue';
import OrdersTableOrder from './OrdersTableOrder.vue';

export default {
    name: 'OrderSearchResults',

    components: {
        SortButton,
        VuePagination,
        OrdersTableOrder,
    },

    props: {
        query: {
            type: Object,
            default: () => ({}),
        },
        icons: {
            type: Array,
            default: () => [],
        },
        title: {
            type: String,
            default: 'Search Results',
        },
    },

    data() {
        return {
            orders: [],
            loading: true,
            perPage: 10,
            statusFilter: '',
            sort: '-created_at',
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            },
            meta: {
                team_count: 0,
                advertiser_count: 0,
                asset_count: 0,
            },
            perPageOptions: [10, 25, 50],
            statusOptions: ['', 'Pending', 'Active', 'Complete', 'Verified', 'Denied'],
        };
    },

    computed: {
        keyPrefix() {
            // Generate a unique key prefix based on search params
            const date = this.getUrlParam('date') || '';
            const filter = this.statusFilter || '';
            const search = this.keyword || '';
            return `${date}${filter}${search}`;
        },

        keyword() {
            return this.query?.filter?.search || this.getUrlParam('search') || '';
        },

        allowedStatuses() {
            return this.query?.filter?.status || '';
        },

        downloadQuery() {
            return {
                allowedStatuses: this.allowedStatuses,
                status: this.statusFilter,
                date: this.getUrlParam('date') || '',
                keyword: this.keyword,
            };
        },
    },

    mounted() {
        // Initialize filter from URL if present
        this.statusFilter = this.getUrlParam('filter') || '';

        // Initialize sort from query if present
        if (this.query?.sort) {
            this.sort = this.query.sort;
        }

        this.loadOrders(1);

        // Listen for refresh events from other components
        Bus.$on('refreshOrdersTable', this.refresh);

        // Listen for creative review closed to refresh
        window.addEventListener('creative-review-closed', this.refresh);
    },

    beforeDestroy() {
        Bus.$off('refreshOrdersTable', this.refresh);
        window.removeEventListener('creative-review-closed', this.refresh);
    },

    methods: {
        getUrlParam(name) {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get(name);
        },

        async loadOrders(page = 1) {
            this.loading = true;

            try {
                const params = {
                    page,
                    perPage: this.perPage,
                    sort: this.sort,
                    allowedStatuses: this.allowedStatuses,
                    filter: {
                        status: this.statusFilter,
                        keyword: this.keyword,
                        date: this.getUrlParam('date') || '',
                    },
                };

                const response = await OrdersTableApi.searchOrders(params);
                this.orders = response.data.data;
                this.pagination = {
                    current_page: response.data.meta.current_page,
                    last_page: response.data.meta.last_page,
                    per_page: response.data.meta.per_page,
                    total: response.data.meta.total,
                };
                this.meta = {
                    team_count: response.data.meta.team_count || 0,
                    advertiser_count: response.data.meta.advertiser_count || 0,
                    asset_count: response.data.meta.asset_count || 0,
                };
            } catch (error) {
                console.error('Failed to load search results:', error);
                this.orders = [];
            } finally {
                this.loading = false;
            }
        },

        sortBy(field) {
            if (this.sort === field) {
                this.sort = `-${field}`;
            } else if (this.sort === `-${field}`) {
                this.sort = field;
            } else {
                this.sort = field;
            }
            this.loadOrders(1);
        },

        goToPage(page) {
            this.loadOrders(page);
        },

        onPerPageChange() {
            this.loadOrders(1);
        },

        onStatusFilterChange() {
            this.loadOrders(1);
        },

        refresh() {
            this.loadOrders(this.pagination.current_page);
        },

        handleOpenReview(creativeIds, orderId, isOrder = false) {
            Bus.$emit('openReviewCreative', creativeIds, orderId, isOrder);
        },

        handleOpenViewCreative(creativeIds, orderId, isOrder = false) {
            Bus.$emit('openViewCreative', creativeIds, orderId, isOrder);
        },
    },
};
</script>

<style scoped>
.orders-search-results-vue {
    min-height: 300px;
}

.opacity-75 {
    opacity: 0.75;
}
</style>